import CryptoJS from "crypto-js";
import moment from "moment";
import { skuRegex, snackConst } from "../constants/Variables";

const ENCRYPTION_KEY = process.env.REACT_APP_ENCRYPTION_KEY;

const encryptPassword = (password) => {
  const encrypted = CryptoJS.AES.encrypt(password, ENCRYPTION_KEY).toString();
  return encrypted;
};

const formattedDate = (date, includeTime) => {
  const format = includeTime ? "DD/MM/YYYY h:mm a" : "DD/MM/YYYY";
  return moment(date || new Date()).format(format);
};

const formattedTime = (date, isAmPm) => {
  const format = isAmPm ? "h:mm a" : "h:mm";
  return moment(date || new Date()).format(format);
};

function calculatePackageDuration(startDate, endDate) {
  const currentDate = new Date();

  const start = new Date(startDate);
  const end = new Date(endDate);

  if (currentDate < start) {
    return "Not start yet";
  }

  const timeDiff = end - currentDate;

  if (timeDiff < 0) {
    return "Expired";
  }

  const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
  const hours = Math.floor(timeDiff / (1000 * 60 * 60));
  const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);

  return { days, hours, minutes, seconds };
}

const getCurrentWeek = () => {
  const current = new Date();
  const week = [];

  // Set date to Monday of the current week
  current.setDate(current.getDate() - current.getDay() + 1);
  for (let i = 0; i < 7; i++) {
    week.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }
  return week;
};

const getStatusBackgroundColor = (status) => {
  switch (status.toLowerCase()) {
    case "complete":
      return { backgroundColor: "#0DBE89" };
    case "return":
      return { backgroundColor: "#F85A5A" };
    case "on the way":
      return { backgroundColor: "#FFEA2F" };
    default:
      return { backgroundColor: "#FFF" };
  }
};

const handleOpenSnackBar = (type, message) => {
  snackConst.snackRef.isOpenSnack({
    snackType: type,
    snackMessage: message,
  });
};

function calculateRemainingDays(endDate) {
  // Get today's date
  const today = new Date();
  // Create a date object from the endDate string
  const end = new Date(endDate);

  // Calculate the difference in milliseconds
  const differenceInMillis = end - today;

  // Convert milliseconds to days
  const daysRemaining = Math.ceil(differenceInMillis / (1000 * 60 * 60 * 24));

  // If days remaining is negative, return 0 (or handle as needed)
  return daysRemaining < 0 ? 0 : daysRemaining;
}

const capitalizeText = (text) => {

  if (!text) return "";
  return text
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const roundTo2Decimal = (value) => Math.floor(value * 100) / 100;

const calculateProductFinalPrice = (p, d, m) => {
  const price = Number(p) || 0; // Cost Price
  const discount = Number(d) || 0; // Discount in amount
  const profitMargin = Number(m) || 0; // Profit %

  // Calculate profit amount
  const profitAmount = (price * profitMargin) / 100;

  // Selling price
  let finalPrice = price + profitAmount - discount;

  // 3 decimal precision
  finalPrice = roundTo2Decimal(finalPrice);

  return finalPrice;
};

const calculateVat = (p, v) => {
  const price = Number(p) || 0; // SP
  const vat = Number(v) || 0;

  let vatAmount = price * (vat / 100);

  vatAmount = roundTo2Decimal(vatAmount);
  return vatAmount;
};

// const calculateLossAmount = (p, d, pm) => {
//   const price = Number(p) || 0;
//   const discount = Number(d) || 0;
//   const profitMargin = Number(pm) || 0;

//   // SP = CP + profit - discount
//   const sellingPrice = price + calculateProfitMargin(price, profitMargin) - discount;

//   // Loss (negative = profit)
//   let lossAmount = price - sellingPrice;

//   return roundTo2Decimal(lossAmount);
// };

const calculateLossAmount = (p, d, pm) => {
  const price = Number(p) || 0;
  const discount = Number(d) || 0;
  const profitMargin = Number(pm) || 0;

  const finalPrice =
    price + calculateProfitMargin(price, profitMargin) - discount;
  let lossAmount = finalPrice - price;
  lossAmount = roundTo2Decimal(lossAmount);
  return lossAmount;
};

const calculateProfitMargin = (p, pm) => {
  const price = Number(p) || 0;
  const profitMargin = Number(pm) || 0;

  let profitAmount = price * (profitMargin / 100);

  return roundTo2Decimal(profitAmount);
};

const calculateDiscountPercentage = (original, final) => {
  const originalPrice = Number(original) || 0;
  const finalPrice = Number(final) || 0;

  // Avoid division by zero
  if (originalPrice === 0) return 0;

  let percentage = ((originalPrice - finalPrice) / originalPrice) * 100;
  percentage = Math.round(percentage);

  return percentage;
};

const convertMinutesToHours = (minutes) => {
  if (minutes < 0) return "Invalid input";

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  const hoursPart = hours > 0 ? `${hours} hour${hours > 1 ? "s" : ""}` : "";
  const minutesPart =
    remainingMinutes > 0
      ? `${remainingMinutes} minute${remainingMinutes > 1 ? "s" : ""}`
      : "";

  return `${hoursPart}${hoursPart && minutesPart ? " " : ""
    }${minutesPart}`.trim();
};

// Helper function to get image dimensions
const getImageDimensions = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => resolve({ width: img.width, height: img.height });
      img.onerror = () => reject(new Error("Failed to load image"));
      img.src = e.target.result;
    };
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
};

function calculateOverAllPurchasePrice(selectedPackageProduct = []) {
  let purchasePrice = 0;
  selectedPackageProduct?.forEach((product) => {
    const productPrice = product.price || 0;
    purchasePrice += productPrice;
  });
  return roundTo2Decimal(purchasePrice);
}

function calculateOverAllFinalAmount(selectedPackageProduct = []) {
  let finalAmount = 0;
  selectedPackageProduct?.forEach((product) => {
    const productPrice = product.finalPrice || 0;
    finalAmount += productPrice;
  });
  return roundTo2Decimal(finalAmount);
}

function calculateOverAllDiscount(selectedPackageProduct = []) {
  let discount = 0;
  selectedPackageProduct?.forEach((product) => {
    const productDiscount = product.discount || 0;
    discount += productDiscount;
  });
  return roundTo2Decimal(discount);
}

function calculateOverAllProfitMargin(selectedPackageProduct = []) {
  let profitMargin = 0;
  selectedPackageProduct?.forEach((product) => {
    const productProfitMargin = product.profitMargin || 0;
    profitMargin += productProfitMargin;
  });
  return roundTo2Decimal(profitMargin);
}

function calculateAverageProfitMargin(selectedPackageProduct = []) {
  let totalPurchasePrice = 0;
  let totalFinalPrice = 0;

  selectedPackageProduct?.forEach((product) => {
    totalPurchasePrice += product.price || 0;
    totalFinalPrice += product.finalPrice || 0;
  });

  if (totalPurchasePrice === 0) return 0;

  const averageProfitMargin =
    ((totalFinalPrice - totalPurchasePrice) / totalPurchasePrice) * 100;

  return roundTo2Decimal(averageProfitMargin);
}

function calculateOverAllEarnAmount(selectedPackageProduct = []) {
  let earnAmount = 0;
  selectedPackageProduct?.forEach((product) => {
    const productEarnAmount = product.earnAmount || 0;
    earnAmount += productEarnAmount;
  });
  return roundTo2Decimal(earnAmount);
}

const calculateOverAllLossAmount = (selectedPackageProduct = []) => {
  let lossAmount = 0;
  selectedPackageProduct?.forEach((product) => {
    const productLossAmount = product.lossAmount || 0;
    lossAmount += productLossAmount;
  });
  return roundTo2Decimal(lossAmount);
};

const calculateOverAllVatAmount = (selectedPackageProduct = []) => {
  let vatAmount = 0;
  selectedPackageProduct?.forEach((product) => {
    const productVatAmount = product.vatAmount || 0;
    vatAmount += productVatAmount;
  });
  return roundTo2Decimal(vatAmount);
};

const calculateSupplierTotalOrderPrice = (product) => {
  let a = 0;
  product.map((v) => {
    a += v.pricing.finalPrice * v.selectedQty;
  });
  return roundTo2Decimal(a);
};

const calculateProductOrderVatAmount = (selectedPackageProduct = []) => {
  let vatAmount = 0;
  selectedPackageProduct?.forEach((product) => {
    const productVatAmount = product?.pricing?.originalVatAmount * product?.selectedQty || 0;
    vatAmount += productVatAmount;
  });
  return roundTo2Decimal(vatAmount);
};

const calculateOrderVatForShippingAndPlatformFee = (
  shippingFee,
  platformFee,
  vat
) => {
  const finalPrice = shippingFee + platformFee;
  const vatAmount = calculateVat(finalPrice, vat);
  return roundTo2Decimal(vatAmount);
};

const calculateLossAmountForPackage = (selectedPackageProduct = []) => {
  let lossAmount = 0;
  selectedPackageProduct?.forEach((product) => {
    const productLossAmount = product.lossAmount || 0;
    lossAmount += productLossAmount;
  });
  return roundTo2Decimal(lossAmount);
};

const calculateEarnAmountForPackage = (selectedPackageProduct = []) => {
  let earnAmount = 0;
  selectedPackageProduct?.forEach((product) => {
    const productEarnAmount = product.earnAmount || 0;
    earnAmount += productEarnAmount;
  });
  return roundTo2Decimal(earnAmount);
};

const decodeHtml = (html) => {
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.documentElement.textContent;
};

const validateVariants = (variants) => {
  const inValidImage = variants.every((variant) => variant.images.length === 0);
  const inValidPrice = variants.every((variant) => variant.price === 0 || variant.price < 0 || isNaN(variant.price));
  const inValidQuantity = variants.every((variant) => variant.quantity === 0 || variant.quantity < 0 || isNaN(variant.quantity));
  const inValidProfitMargin = variants.every((variant) => variant.profitMargin < 0 || isNaN(variant.profitMargin));
  const inValidDiscount = variants.every((variant) => variant.discount < 0 || isNaN(variant.discount));
  const inValidSku = variants.every((variant) => variant.sku === "" || !skuRegex.test(variant.sku));

  const errors = [];

  if (inValidImage) errors.push("validation.variant.inValidImage");
  if (inValidPrice) errors.push("validation.variant.inValidPrice");
  if (inValidQuantity) errors.push("validation.variant.inValidQuantity");
  if (inValidProfitMargin) errors.push("validation.variant.inValidProfitMargin");
  if (inValidDiscount) errors.push("validation.variant.inValidDiscount");
  if (inValidSku) errors.push("validation.variant.inValidSku");
  return errors;
};

const overAllProductQuantity = (variants) => {
  let quantity = 0;
  variants.forEach((variant) => {
    quantity += variant.quantity;
  });
  return quantity;
};

const getDeliveryFrequencyDuration = (deliveryFrequency) => {
  if (deliveryFrequency === "weekly") return "everyWeek";
  if (deliveryFrequency === "biweekly") return "every2weeks";
  if (deliveryFrequency === "monthly") return "everyMonth";
  if (deliveryFrequency === "quarterly") return "every3months";
  return "notSheduled";
};

const getPlanStatusStyle = (status) => {
  const normalizedStatus = status?.toLowerCase();

  if (normalizedStatus === "active") {
    return {
      color: "#2E7D32",
      bgColor: "#E8F5E9",
      dotColor: "#66BB6A",
    };
  }

  if (normalizedStatus === "inactive") {
    return {
      color: "#D05D6B",
      bgColor: "#FDEFF2",
      dotColor: "#F06273",
    };
  }

  if (normalizedStatus === "paused") {
    return {
      color: "#2563EB",
      bgColor: "#EFF6FF",
      dotColor: "#60A5FA",
    };
  }

  return {
    color: "#5F6B7A",
    bgColor: "#F3F4F6",
    dotColor: "#9CA3AF",
  };
};

const calculatePercentageForProduct = (original, final) => {
  const originalPrice = Number(original) || 0;
  const finalPrice = Number(final) || 0;

  // Avoid division by zero
  if (originalPrice === 0) return 0;

  let percentage = ((originalPrice - finalPrice) / originalPrice) * 100;
  percentage = Math.round(percentage);

  return percentage;
};

const calculateVatForOne = (p, v) => {
  const price = Number(p) || 0; // SP
  const vat = Number(v) || 0;

  let vatAmount = price * (vat / 100);

  vatAmount = roundTo2Decimal(vatAmount);
  return vatAmount;
};



/**
 * Calculate all product pricing values including VAT and discount
 * @param {number} finalPrice - The final price of the product (before VAT)
 * @param {number} discount - The discount amount
 * @param {number} taxRate - The VAT/tax rate (default: 21)
 * @returns {object} Object containing all calculated pricing values
 */
const calculateProductPricing = (finalPrice, discount = 0, taxRate = 21) => {
  const productPrice = Number(finalPrice) || 0;
  const discountAmount = Number(discount) || 0;
  const vatRate = Number(taxRate) || 21;

  // Calculate VAT amount
  const vatAmount = calculateVatForOne(productPrice, vatRate);

  // Calculate price with VAT
  const productPriceWithVat = roundTo2Decimal(productPrice + vatAmount);

  // Calculate price with discount (original price before discount)
  const productPriceWithDiscount = roundTo2Decimal(
    productPriceWithVat + discountAmount
  );

  // Calculate discount percentage
  const discountPercentage = calculatePercentageForProduct(
    productPriceWithDiscount,
    productPriceWithVat
  );

  // Check if discount exists
  const isDiscount = discountAmount > 0;

  return {
    productPrice: roundTo2Decimal(productPrice),
    vatAmount: roundTo2Decimal(vatAmount),
    productPriceWithVat: roundTo2Decimal(productPriceWithVat),
    productPriceWithDiscount: roundTo2Decimal(productPriceWithDiscount),
    discountPercentage: roundTo2Decimal(discountPercentage),
    isDiscount,
  };
};

const getVariantLabel = (attributes, currentLanguage) => {
  if (!attributes || typeof attributes !== "object") {
    return "Selected variant";
  }

  const entries = Object.entries(attributes);
  if (entries.length === 0) return "Selected variant";

  return entries
    .map(([key, val]) => ` ${val?.label[currentLanguage] || val?.label || val?.value || "-"}`)
    .join(", ");
};


export {
  encryptPassword,
  formattedDate,
  formattedTime,
  calculatePackageDuration,
  getStatusBackgroundColor,
  getCurrentWeek,
  handleOpenSnackBar,
  calculateRemainingDays,
  capitalizeText,
  calculateProductFinalPrice,
  convertMinutesToHours,
  getImageDimensions,
  calculateVat,
  calculateLossAmount,
  calculateProfitMargin,
  roundTo2Decimal,
  // roundTo2Decimal,
  calculateDiscountPercentage,
  calculateOverAllPurchasePrice,
  calculateOverAllFinalAmount,
  calculateOverAllDiscount,
  calculateOverAllProfitMargin,
  calculateAverageProfitMargin,
  calculateOverAllEarnAmount,
  calculateOverAllLossAmount,
  calculateOverAllVatAmount,
  calculateSupplierTotalOrderPrice,
  calculateProductOrderVatAmount,
  calculateOrderVatForShippingAndPlatformFee,
  calculateLossAmountForPackage,
  calculateEarnAmountForPackage,
  decodeHtml,
  validateVariants,
  overAllProductQuantity,
  getDeliveryFrequencyDuration,
  getPlanStatusStyle,
  calculateProductPricing,
  getVariantLabel,
};
